<template>
    <vx-table-row class="outline outline-2 outline-offset-4 outline-amber-500">
        <vx-table-row-title class="font-normal!">
            <select-org-unit
                v-if="props.type === 'ou'"
                ref="targetSelectRef"
                v-model="selectedTarget"
                :teamId="props.teamId"
                :clearable="false"
                data-test="target-select"
            />
            <select-group
                v-else
                ref="targetSelectRef"
                v-model="selectedTarget"
                :teamId="props.teamId"
                :reduce="(option: Record<string, unknown>) => option"
                :clearable="false"
                data-test="target-select"
            />
        </vx-table-row-title>

        <vx-table-row-item class="min-w-[100px]">
            <template v-slot:label> Type: </template>
            <vx-badge v-if="props.type === 'ou'" size="xs" color="violet" data-test="type-badge">
                <font-awesome-icon :icon="faBuildingFlag"></font-awesome-icon>
                OU
            </vx-badge>
            <vx-badge v-else size="xs" color="slate" data-test="type-badge">
                <font-awesome-icon :icon="faGrid2Plus" class="-mx-0.5"></font-awesome-icon>
                Group
            </vx-badge>
        </vx-table-row-item>

        <vx-table-row-item class="min-w-[220px]">
            <template v-slot:label>
                <span class="inline-flex items-center gap-1">
                    Role:
                    <group-role-permissions-popover v-if="props.type === 'group'" />
                </span>
            </template>
            <vx-selectable
                v-model="selectedRole"
                :reduce="(option: { value: string }) => option.value"
                :options="roleOptions"
                :clearable="false"
                :disabled="props.saving"
                data-test="role-select"
                class="w-[200px]"
            />
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button
                data-test="cancel-add"
                aria-label="Cancel"
                @click="emit('cancel')"
                type="button"
                color="muted"
                outline
            >
                <font-awesome-icon :icon="faCircleX"></font-awesome-icon>
            </vx-button>
            <vx-button
                data-test="confirm-add"
                class="min-w-[125px]"
                @click="save"
                type="button"
                color="primary"
                :loading="props.saving"
                :disabled="!canSave"
            >
                Add Target
                <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script lang="ts" setup>
import { faBuildingFlag, faCirclePlus, faCircleX, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxSelectable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, nextTick, onMounted, ref } from 'vue';
import { orgUnitRolesOptions, groupRolesOptions, DEFAULT_ORG_UNIT_ROLE, DEFAULT_GROUP_ROLE } from '~/constants/roles';
import SelectOrgUnit from '~/components/general/SelectOrgUnit.vue';
import SelectGroup from '~/components/general/SelectGroup.vue';
import GroupRolePermissionsPopover from '~/components/settings/teams/groups/GroupRolePermissionsPopover.vue';

const props = defineProps<{
    type: 'ou' | 'group';
    saving: boolean;
    teamId: number;
}>();

const emit = defineEmits<{
    saved: [payload: { targetId: string | number; name: string; type: 'ou' | 'group'; role: string }];
    cancel: [];
}>();

const targetSelectRef = ref<InstanceType<typeof SelectOrgUnit> | InstanceType<typeof SelectGroup> | null>(null);
const selectedTarget = ref<Record<string, unknown> | null>(null);
const selectedRole = ref(props.type === 'ou' ? DEFAULT_ORG_UNIT_ROLE : DEFAULT_GROUP_ROLE);

const roleOptions = computed(() => (props.type === 'ou' ? orgUnitRolesOptions() : groupRolesOptions()));

const getTargetId = (target: Record<string, unknown> | null): string | number | undefined => {
    if (!target) {
        return undefined;
    }
    if (props.type === 'ou') {
        return target.id as number;
    }
    return (target.value || target.group_id) as string;
};

const getTargetName = (target: Record<string, unknown> | null): string => {
    if (!target) {
        return '';
    }
    return (target.name as string) || '';
};

const canSave = computed(() => {
    return !!getTargetId(selectedTarget.value) && !!selectedRole.value && !props.saving;
});

const save = () => {
    const targetId = getTargetId(selectedTarget.value);
    const role = selectedRole.value;

    if (!targetId || !role || props.saving) {
        return;
    }

    emit('saved', {
        targetId,
        name: getTargetName(selectedTarget.value),
        type: props.type,
        role,
    });
};

onMounted(async () => {
    await nextTick();
    (targetSelectRef.value as { $el: HTMLElement } | null)?.$el?.querySelector('input')?.focus();
});
</script>
